import React, { useEffect } from "react";
import { motion } from 'framer-motion';
import { CalendarDays, GraduationCap, BookOpen } from 'lucide-react';
import uctcLogo from '/img/logo.png';

export default function AdmissionRequirement({ 
  accent = "#115e32",
  pageTitle = "Admission Requirements and Schedule | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] flex flex-col items-center px-4 md:px-8 lg:px-16 py-16">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-3xl mb-16"
      >
        <img src={uctcLogo} alt="UCTC Logo" className="mx-auto w-28 h-auto mb-6" />
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Admission Requirements</h1>
        <p className="text-lg text-gray-600">Explore the eligibility criteria for our Bachelor and Master programs and check our admission schedule.</p>
      </motion.div>

      {/* Bachelor Program Requirements */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg p-8 mb-12 max-w-4xl w-full border-t-4 border-yellow-400"
      >
        <div className="flex items-center gap-3 mb-4">
          <GraduationCap className="text-yellow-500" size={28} />
          <h2 className="text-2xl font-semibold text-gray-800">Admission Requirement – Bachelor Program</h2>
        </div>
        <p className="text-gray-700 mb-2 font-medium">Admission Requirements For All Bachelor Programs</p>
        <ul className="list-disc list-inside text-gray-600 space-y-2">
          <li>Minimum GPA of 2.5 both in SSC and HSC examinations or equivalent.</li>
          <li>Or, at least one GPA of 2.00 but aggregate GPA of 6.00 in SSC and HSC.</li>
          <li>Or, as per UGC rules the requirements are O'Level 5 subjects, A'Level 2 subjects (4 subjects with minimum GPA 4.0 (Grade-B) and 3 subjects with minimum GPA 3.5 (Grade-C)).</li>
          <li>For admission to B.Sc. in EEE/CSE/Mechanical/Civil Engineering programs, students must have passed in Physics and Mathematics in HSC/A-Level.</li>
          <li>Or, International Baccalaureate/American High School Diploma.</li>
          <li>Or, sons/daughters of freedom fighters with an aggregate GPA of 5.00 in SSC and HSC.</li>
          <li>GED result will not be accepted.</li>
        </ul>
      </motion.div>

      {/* Master Program Requirements */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg p-8 mb-12 max-w-4xl w-full border-t-4 border-teal-400"
      >
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="text-teal-500" size={28} />
          <h2 className="text-2xl font-semibold text-gray-800">Admission Requirement – Master Program</h2>
        </div>
        <ul className="list-disc list-inside text-gray-600 space-y-2">
          <li>Successful completion of at least a Bachelor degree with minimum CGPA of 2.5 or equivalent in relevant discipline from a reputed university.</li>
          <li>Qualifying in a university admission test or a minimum score of 550 in TOEFL and 500 in GMAT.</li>
          <li>Work experience after graduation in an executive position is preferred but not essential (For regular MBA only).</li>
        </ul>
      </motion.div>

      {/* Admission Schedule */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        viewport={{ once: true }}
        className="max-w-3xl w-full grid md:grid-cols-2 gap-8"
      >
        {[{
          title: 'Spring Session',
          desc: 'January – June',
          color: 'from-yellow-400 to-yellow-300'
        }, {
          title: 'Autumn Session',
          desc: 'July – December',
          color: 'from-[#1f6da8] to-teal-300'
        }].map((session, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.05, rotate: 1 }}
            className={`bg-gradient-to-br ${session.color} text-white p-8 rounded-2xl shadow-lg flex flex-col items-center transition-transform`}
          >
            <CalendarDays size={48} className="mb-3" />
            <h3 className="text-2xl font-semibold mb-1">{session.title}</h3>
            <p className="text-lg font-medium">{session.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
